export type ProviderId = 'anthropic' | 'openai' | 'openrouter' | 'demo';
export type ProposalStatus = 'pending' | 'accepted' | 'rejected' | 'superseded';

/**
 * A change proposed by the model through the `propose_changes` tool. It can replace the code, the spec,
 * or both; whatever it leaves out stays as it is.
 */
export interface CodeProposal {
  id: string;
  summary: string;
  status: ProposalStatus;
  /** Complete new code, when the proposal changes the code. */
  code?: string;
  language?: string;
  /** Complete new spec, when the proposal changes the spec. */
  spec?: string;
}

export type MessagePart =
  | { type: 'text'; text: string }
  /** A readable summary of the model's reasoning, shown folded. */
  | { type: 'reasoning'; text: string }
  | { type: 'proposal'; proposal: CodeProposal }
  | { type: 'error'; text: string };

/** What was attached to a prompt, kept so the history stays faithful to what the model saw. */
export interface PromptContext {
  code?: { language: string; code: string };
  spec?: { text: string; state: SpecState };
  output?: { text: string; failed: boolean };
}

/**
 * The provider's own record of an assistant turn (for example thinking blocks with signatures, or
 * encrypted reasoning items). It is replayed verbatim when the next request goes to the same provider
 * and model, which keeps reasoning intact across tool calls; any other model gets the portable parts.
 */
export interface NativeTurn {
  provider: ProviderId;
  model: string;
  items: unknown[];
}

/**
 * Exactly what a prompt sent: its text with the card context, and the answers to the previous turn's
 * proposals. Stored at send time and replayed unchanged, because history must be append-only: rewriting an
 * earlier message breaks prompt caching and invalidates the model's saved reasoning.
 */
export interface SentPrompt {
  text: string;
  toolResults: { id: string; content: string }[];
}

export interface Usage {
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens?: number;
  /** The model that answered, as the provider reported it. A router or a fallback can differ from the one asked for. */
  model?: string;
}

export interface CardMessage {
  id: string;
  role: 'user' | 'assistant';
  parts: MessagePart[];
  createdAt: number;
  context?: PromptContext;
  /** Set when the prompt came from a suggestion or a UI action rather than typed text. */
  action?: string;
  sent?: SentPrompt;
  native?: NativeTurn;
  usage?: Usage;
}

/** A proposal waiting for a decision. The card holds the proposed versions; these are what they replace. */
export interface PendingReview {
  proposalId: string | null;
  original: string;
  originalSpec: string;
}

/** Where the spec stands relative to the code. */
export type SpecState = 'empty' | 'in-step' | 'code-changed' | 'spec-changed' | 'unchecked';

/** Fingerprints of the code and spec at the last moment they were known to agree. */
export interface SpecSync {
  code: string;
  spec: string;
}

/**
 * A Cue card: one piece of work. It holds the conversation, the code in the editor, and the spec that
 * describes what the code should do.
 */
export interface Card {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: CardMessage[];
  /** Current editor contents. While a review is pending this is the proposed version. */
  code: string;
  language: string;
  /** Plain-language description of what the code does. While a review is pending this is the proposed version. */
  spec: string;
  specSync: SpecSync | null;
  /** The latest next cues, kept so they are there when the card is opened again. A new turn clears them. */
  suggestions?: string[];
  review: PendingReview | null;
}

export type CardSummary = Pick<Card, 'id' | 'title' | 'createdAt' | 'updatedAt' | 'language'>;

export type Theme = 'system' | 'light' | 'dark';
export type KeyStorage = 'local' | 'session';

/** How hard the model should think. Mapped to each provider's own effort or reasoning setting. */
export type Effort = 'low' | 'medium' | 'high';

export interface Settings {
  provider: ProviderId;
  /** Selected model per provider, so switching providers keeps each choice. */
  models: Partial<Record<ProviderId, string>>;
  systemPrompt: string;
  theme: Theme;
  keyStorage: KeyStorage;
  effort: Effort;
  /** Ask the model for suggested next prompts after each turn. */
  suggestions: boolean;
}

export const DEFAULT_TITLE = 'Untitled card';
